import { ChangeDetectorRef, DestroyRef, Directive, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, ControlValueAccessor, TouchedChangeEvent, ValidationErrors, Validator } from '@angular/forms';
import { distinctUntilChanged, tap } from 'rxjs';
import { filter } from 'rxjs/operators';

@Directive()
export abstract class CompositeValueAccessor<T> implements OnInit, ControlValueAccessor, Validator {
  protected readonly destroyRef: DestroyRef = inject(DestroyRef);
  protected readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  protected abstract readonly form: AbstractControl<unknown>;

  protected readonly invalidMesssage: string = 'Form is invalid';

  ngOnInit(): void {
    this.form.valueChanges
      .pipe(
        distinctUntilChanged(),
        tap(() => this.onChange(this.getOutputValue())),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // Propagate the touched state to parent form
    this.form.events
      .pipe(
        filter((event) => event instanceof TouchedChangeEvent && event.touched),
        tap(() => this.onTouched()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onTouched: () => void = () => {};

  onChange: (value: T) => void = () => {};

  writeValue(value: T): void {
    value && this.form.setValue(value, { emitEvent: false });
    this.cdr.markForCheck();
  }

  getOutputValue(): T {
    return this.form.getRawValue() as T;
  }

  registerOnChange(onChange: (value: T) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled === this.form.disabled) {
      return;
    }

    isDisabled ? this.form.disable() : this.form.enable();
  }

  validate(): ValidationErrors | null {
    if (this.form.valid) {
      return null;
    }

    return this.form.errors || { invalidForm: { valid: false, message: this.invalidMesssage } };
  }
}
